import { Controller } from "@hotwired/stimulus"
import debounce from "lodash/debounce"
import Swiper from "swiper/bundle"
import Radar from "radar-sdk-js"

export default class extends Controller {
  static values = {
    minLat: Number,
    maxLat: Number,
    minLng: Number,
    maxLng: Number,
    filter: String,
    radarPublishableKey: String
  }
  static targets = ["list", "listContainer", "mapContainer", "changeLocation", "filters", "showFilters", "hideFilters"]

  async connect() {
    new Swiper(".swiper.community-finder-filters", {
      grabCursor: true,
      slidesPerView: "auto",
      spaceBetween: 0
    })
    this.initializeCommunitySwipers()

    this.mapMarkers = []
    const { Map } = await google.maps.importLibrary("maps")

    this.map = new Map(document.getElementById("community-finder-map"), {
      center: {
        lat: (this.maxLatValue - this.minLatValue) / 2.0 + this.minLatValue,
        lng: (this.maxLngValue - this.minLngValue) / 2.0 + this.minLngValue
      },
      zoom: 10,
      minZoom: 8,
      maxZoom: 17,
      zoomControl: window.innerWidth >= 1024,
      gestureHandling: "greedy",
      disableDefaultUI: true,
      mapId: "98e479dfeea5a4af"
    })

    this.map.addListener(
      "idle",
      debounce(
        () => {
          this.minLatValue = this.map.getBounds().getSouthWest().lat()
          this.minLngValue = this.map.getBounds().getSouthWest().lng()
          this.maxLatValue = this.map.getBounds().getNorthEast().lat()
          this.maxLngValue = this.map.getBounds().getNorthEast().lng()
          this.updateCommunities()
        },
        1000,
        { leading: true, trailing: true }
      )
    )

    this.addMyLocationControl()

    await this.updateMapMarkers()
  }

  initializeCommunitySwipers() {
    new Swiper(".swiper.community-card-footer", {
      grabCursor: true,
      slidesPerView: "auto",
      spaceBetween: 0
    })
  }

  addMyLocationControl() {
    if (!navigator.geolocation) return

    const controlButton = document.createElement("button")
    controlButton.type = "button"
    controlButton.classList.add("community-finder-my-location")
    controlButton.title = "Go to my location"
    controlButton.innerHTML = '<i class="fa-solid fa-location-crosshairs"></i>'

    controlButton.addEventListener("click", () => {
      controlButton.disabled = true
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          // Check if we have a recent geolocation cookie (within the last hour)
          const shouldUpdateLocation = this.shouldUpdateGeolocation()

          if (shouldUpdateLocation) {
            // Reverse geocode, update the location cookie/user, and reload the page
            await this.updateLocationFromGeolocation(position)
          } else {
            this.map.panTo({
              lat: position.coords.latitude,
              lng: position.coords.longitude
            })
          }

          controlButton.disabled = false
        },
        () => {
          controlButton.disabled = false
        }
      )
    })

    this.map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(controlButton)
  }

  shouldUpdateGeolocation() {
    try {
      const cookieValue = this.getCookie("current_location")
      if (!cookieValue) return true

      const location = JSON.parse(cookieValue)
      if (location.source !== "geolocation") return true

      if (!location.timestamp) return true

      const timestamp = new Date(location.timestamp)
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000)
      return timestamp < oneHourAgo
    } catch (e) {
      // If parsing fails, we should update
      return true
    }
  }

  getCookie(name) {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) {
      return decodeURIComponent(parts.pop().split(";").shift())
    }
    return null
  }

  async updateLocationFromGeolocation(position) {
    try {
      if (this.radarPublishableKeyValue) {
        Radar.initialize(this.radarPublishableKeyValue)
      }

      const result = await Radar.reverseGeocode({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude
      })

      if (result && result.addresses && result.addresses.length > 0) {
        const { city, stateCode, postalCode, countryCode } = result.addresses[0]
        const locationData = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          city: city,
          state: stateCode,
          zip: postalCode,
          countryCode: countryCode,
          source: "geolocation",
          accuracy: position.coords.accuracy
        }

        const response = await fetch("/api/front_end/current_location", {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest"
          },
          body: JSON.stringify(locationData)
        })

        if (response.ok) {
          window.location.reload()
          return
        }
      }
    } catch (e) {
      console.error("Error updating location from geolocation", e)
    }

    // If we didn't reload, pan to the location
    this.map.panTo({
      lat: position.coords.latitude,
      lng: position.coords.longitude
    })
  }

  showMap(event) {
    event?.preventDefault()

    if (this.hasListContainerTarget) {
      this.listContainerTarget.classList.add("hidden")
    }

    if (this.hasMapContainerTarget) {
      this.mapContainerTarget.classList.remove("hidden")
    }
  }

  showList(event) {
    event?.preventDefault()

    if (this.hasListContainerTarget) {
      this.listContainerTarget.classList.remove("hidden")
    }

    if (this.hasMapContainerTarget) {
      this.mapContainerTarget.classList.add("hidden")
    }
  }

  showChangeLocation(event) {
    event?.preventDefault()

    if (this.hasListTarget) {
      this.listTarget.classList.add("hidden")
    }

    if (this.hasChangeLocationTarget) {
      this.changeLocationTarget.classList.remove("hidden")
      this.changeLocationTarget.querySelector("input")?.focus()
    }
  }

  hideChangeLocation(event) {
    event?.preventDefault()

    if (this.hasListTarget) {
      this.listTarget.classList.remove("hidden")
    }

    if (this.hasChangeLocationTarget) {
      this.changeLocationTarget.classList.add("hidden")
    }
  }

  showFilters(event) {
    event.preventDefault()

    if (this.hasFiltersTarget) {
      this.filtersTarget.classList.add("show-all")
    }

    if (this.hasShowFiltersTarget) {
      this.showFiltersTarget.style.display = "none"
    }

    if (this.hasHideFiltersTarget) {
      this.hideFiltersTarget.style.display = "flex"
    }
  }

  hideFilters(event) {
    event.preventDefault()

    if (this.hasFiltersTarget) {
      this.filtersTarget.classList.remove("show-all")
    }

    if (this.hasShowFiltersTarget) {
      this.showFiltersTarget.style.display = "flex"
    }

    if (this.hasHideFiltersTarget) {
      this.hideFiltersTarget.style.display = "none"
    }
  }

  async updateFilter(event) {
    event?.preventDefault()

    const { filter } = event.currentTarget.dataset

    if (this.filterValue === filter) {
      this.filterValue = null
    } else {
      this.filterValue = filter
    }

    await this.updateCommunities()
  }

  async clearFilter(event) {
    event?.preventDefault()

    this.filterValue = null

    await this.updateCommunities()
  }

  async updateCommunities() {
    const params = new URLSearchParams({
      min_lat: this.minLatValue,
      min_lng: this.minLngValue,
      max_lat: this.maxLatValue,
      max_lng: this.maxLngValue
    })

    if (this.selectedCommunityIdValue) {
      params.append("selected_community_id", this.selectedCommunityIdValue)
    }

    if (this.filterValue && this.filterValue !== "null" && this.filterValue !== "undefined") {
      params.append("filter", this.filterValue)
    }

    if (this.hasListTarget) {
      document.getElementById("community-finder-list-content").innerHTML = "<div class='loading'>Loading...</div>"
      try {
        const path = `${window.location.pathname}?${params.toString()}`
        const response = await fetch(path, {
          headers: {
            "X-Requested-With": "XMLHttpRequest"
          }
        })
        if (response.ok) {
          this.listTarget.innerHTML = await response.text()
          this.initializeCommunitySwipers()

          // We want to the update the URL in case people want to share the link,
          // but we don't want the map bounds in the URL
          const url = new URL(`${window.location.origin}${window.location.pathname}`)
          if (this.filterValue && this.filterValue !== "null" && this.filterValue !== "undefined") {
            url.searchParams.set("filter", this.filterValue)
          }
          window.Turbo.navigator.history.replace(url)
        } else {
          console.error(`Could not update communities - HTTP ${response.status}`)
          this.listTarget.innerHTML = `<div class="error">An application error has occurred, please reload this page to try again</div>`
        }
      } catch (e) {
        console.error("Could not update communities", e)
        this.listTarget.innerHTML = `<div class="error">A network error has occurred, please reload this page to try again</div>`
      }
    } else {
      console.warn("Cannot update communities, no list container target")
      this.listTarget.innerHTML = `<div class="error">An error has occurred, please reload this page to try again</div>`
    }

    await this.updateMapMarkers()
  }

  async updateMapMarkers() {
    this.mapMarkers.forEach((marker) => {
      marker.map = null
    })
    this.mapMarkers = []
    const { InfoWindow } = await google.maps.importLibrary("maps")
    const { AdvancedMarkerElement } = await google.maps.importLibrary("marker")

    const infoWindow = new InfoWindow({ headerDisabled: true })
    infoWindow.addListener("closeclick", () => {
      this.selectedCommunityId = null
    })

    document.querySelectorAll(".community-card").forEach((el) => {
      const c = el.dataset
      const communityId = c.id ? parseInt(c.id) : null
      const featured = c.featured === "true"

      const content = document.createElement("div")
      content.id = `community-finder-map-marker-${communityId}`
      content.title = c.name
      content.classList.add("community-finder-map-marker")
      if (featured) {
        content.classList.add("featured")
      }
      if (this.selectedCommunityId === communityId) {
        content.classList.add("selected")
      }
      content.textContent = c.price

      const marker = new AdvancedMarkerElement({
        map: this.map,
        position: { lat: parseFloat(c.lat), lng: parseFloat(c.lng) },
        content
      })

      const showInfoWindow = () => {
        const details = document.getElementById(`community-${communityId}-details`)
        if (details) {
          this.selectedCommunityId = communityId
          const content = `<a href="/golf-leagues/${c.slug}" style="text-decoration: none">${details.outerHTML}</a>`
          infoWindow.setContent(content)
          infoWindow.open(this.map, marker)
        } else if (infoWindow.isOpen) {
          this.selectedCommunityId = null
          infoWindow.close()
        }
      }

      if (this.selectedCommunityId === communityId) {
        showInfoWindow()
      }

      marker.addListener("gmp-click", () => {
        document.querySelectorAll(".community-finder-map-marker").forEach((el) => {
          if (el.id === content.id) {
            if (el.classList.contains("selected")) {
              infoWindow.close()
              el.classList.remove("selected")
            } else {
              el.classList.add("selected")
              this.map.panTo(marker.position)
              showInfoWindow()
            }
          } else {
            el.classList.remove("selected")
          }
        })
      })

      this.mapMarkers.push(marker)
    })
  }
}
